import React, { useEffect, useState } from 'react';
import { Chip, Stack, TextField } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';

import { TextOperationService } from '../../../services';
import { mergePromptWithSwatches } from '../../shared/panels/swatchSelection';
import { useAgentResultStore } from '../../../store/agentResultStore';
import {
  DEFAULT_GENERATE_TEXT_STATE,
  buildGenerateTextRequest,
  type GenerateTextFormState,
} from './buildRequests';
import { useRunTextOperation } from './useTextOperationMutation';
import { TEXT_OPERATION_CATALOG } from './operationCatalog';
import { PanelScaffold } from './PanelScaffold';
import type { TextPanelProps } from './panelProps';

export const GeneratePanel: React.FC<TextPanelProps> = ({
  ensureSession,
  isCreatingSession = false,
  documentId,
  swatchPrompt,
  studioParams,
  referenceImageId,
  sectionId,
  promptSeed,
  promptSeedKey,
  onResult,
}) => {
  const [state, setState] = useState<GenerateTextFormState>(DEFAULT_GENERATE_TEXT_STATE);
  const { run, mutation, validationError } = useRunTextOperation(
    TextOperationService.generate,
    ensureSession,
    documentId,
    { onResult },
  );

  // Storyteller: when a section is (re)selected, seed its synopsis into the prompt.
  // Keyed on promptSeedKey so re-selecting the same section re-seeds.
  useEffect(() => {
    if (promptSeed === undefined) return;
    setState(s => ({ ...s, prompt: promptSeed }));
  }, [promptSeed, promptSeedKey]);

  // Gap 4 — agent cold-start handoff. When the user clicks "Use as prompt" inside
  // an AgentInvocationCard, the agent's output lands here and seeds the prompt
  // field. Single-slot consume-on-arrival; mirrors ImageLab's GeneratePanel.
  const pendingAgentHandoff = useAgentResultStore(s => s.pending);
  useEffect(() => {
    if (!pendingAgentHandoff) return;
    const consumed = useAgentResultStore.getState().consumeHandoff();
    if (!consumed) return;
    setState(s => ({ ...s, prompt: consumed.promptText }));
  }, [pendingAgentHandoff]);

  const handleSubmit = () =>
    run((sid, docId) =>
      buildGenerateTextRequest(
        { ...state, prompt: mergePromptWithSwatches(state.prompt, swatchPrompt ?? '') },
        sid,
        docId,
        studioParams,
        referenceImageId,
        sectionId,
      ),
    );

  const promptHasFragments = (studioParams?.promptFragments?.length ?? 0) > 0;
  const submitDisabled =
    mutation.isPending ||
    isCreatingSession ||
    (state.prompt.trim().length === 0 && !promptHasFragments);

  return (
    <PanelScaffold
      meta={TEXT_OPERATION_CATALOG.Generate}
      validationError={validationError}
      mutationError={mutation.isError ? mutation.error : null}
      busy={mutation.isPending}
      starting={isCreatingSession}
      submitDisabled={submitDisabled}
      submitLabel="Generate"
      busyLabel="Generating…"
      onSubmit={handleSubmit}
    >
      {referenceImageId && (
        <Chip
          icon={<ImageIcon />}
          label="Captioning attached image"
          size="small"
          color="primary"
          variant="outlined"
          sx={{ alignSelf: 'flex-start' }}
        />
      )}
      <TextField
        label="Prompt"
        value={state.prompt}
        onChange={e => setState(s => ({ ...s, prompt: e.target.value }))}
        multiline
        minRows={2}
        maxRows={6}
        size="small"
        fullWidth
        required={!promptHasFragments}
        helperText={swatchPrompt ? `Swatches applied: ${swatchPrompt}` : undefined}
      />
      <Stack direction="row" spacing={1}>
        <TextField
          label="Target words"
          type="number"
          value={state.targetWordCount || ''}
          onChange={e => setState(s => ({ ...s, targetWordCount: Number(e.target.value) }))}
          size="small"
          placeholder="auto"
          inputProps={{ min: 0, max: 2000, step: 50 }}
          // A number input sizes to its content, which is narrower than the
          // "Target words" label — the outlined legend then clips it. Fill the
          // row instead, with a floor so it still fits in a narrow rail.
          fullWidth
          sx={{ minWidth: 150 }}
        />
      </Stack>
    </PanelScaffold>
  );
};
